<template>
  <!-- 
    This component functions as a re-usable utility for controls in the utility bar. 
    A control is an icon with a label that either opens a link or a popover when clicked 
  -->
  <div class="nav-utility-bar-control">
    <component
      :is="htmlElement"
      ref="utilityBarControlBtn"
      v-tippy="tippyConfig"
      :aria-haspopup="!isLink ? true : false"
      :href="href"
      class="text-white leading-none hover:text-white flex items-center"
      :tabindex="!isCompactView ? 0 : -1"
      :aria-controls="!isLink ? `${popoverHtml}-list` : null"
      @show="isLink ? null : onShow"
    >
      <div class="nav-utility-bar-control__icon relative">
        <slot name="icon">
          <pdl-icon class="self-center" :name="icon" :label="icon" />
          <pdl-badge v-if="hasBadge" qaid="nav-utility-bar-control-badge" class="absolute right-0 top-0" />
        </slot>
      </div>
      <div class="text-md font-medium ml-25 lg:sr-only">
        <slot name="title" />
      </div>
    </component>
    <template :ref="id">
      <div ref="MyAccountPopover">
        <slot v-if="!isLink" v-bind="{notifications, id}" name="popover" />
      </div>
    </template>
  </div>
</template>

<script>
import {mapState, mapGetters} from 'vuex';
import {PdlBadge} from '@pedal/pdl-badge';
import {PdlIcon} from '@pedal/pdl-icon';
import AccountSelector from '@/components/account-selector/AccountSelector.vue';
import B2bMiniCart from '@/components/mini-cart/b2b/B2bMiniCart.vue';
import {directive} from 'vue-tippy';
import {focusTrap, getAllFocusableElements} from '@/utils/accessibility/a11y-utils';

export default {
  name: 'NavUtilityBarControl',
  /* eslint-disable-next-line vue/no-unused-components */
  directives: {
    tippy: directive,
  },
  components: {PdlBadge, PdlIcon, AccountSelector, B2bMiniCart},

  props: {
    /**
     * The icon to display. The icon slot overrides this if used.
     */
    icon: {
      type: String,
      default: undefined,
    },
    /**
     * The popover theme to apply
     */
    theme: {
      type: String,
      default: undefined,
    },
    /**
     * The href for the link. If used, the element will render as an anchor without v-tippy
     */
    href: {
      type: String,
      default: undefined,
    },
    /**
     * The ID of the html element to use as the popover's content
     */
    popoverHtml: {
      type: String,
      default: undefined,
    },
    id: {
      type: String,
      default: '',
    },
  },
  computed: {
    ...mapState('global', ['isCompactView']),
    ...mapGetters('notifications', ['notifications']),
    isLink() {
      return this.href;
    },
    isNotificationControl() {
      return this.icon === 'notifications';
    },
    htmlElement() {
      return this.isLink ? 'a' : 'button';
    },
    hasBadge() {
      return this.isNotificationControl && this.notifications !== null
        ? this.notifications.filter((notification) => !notification.readStatus).length > 0
        : false;
    },
    tippyConfig() {
      return {
        content: this.getPopoverHtml,
        animateFill: true,
        animation: 'fade',
        arrow: true,
        allowHTML: true,
        interactive: true,
        trigger: 'click',
        theme: this.theme,
        placement: 'top-start',
        appendTo: () => document.body,
        popperOptions: {
          positionFixed: true,
          modifiers: [
            {
              name: 'flip',
              options: {
                fallbackPlacements: ['top', 'bottom-end'],
              },
            },
          ],
        },
        onShow: this.isLink ? null : this.onShow,
        onShown: this.focusPopoverContent,
        onHidden: this.revertFocusBackToComponent,
      };
    },
  },
  methods: {
    getPopoverHtml() {
      return this.$refs.MyAccountPopover;
    },
    revertFocusBackToComponent() {
      this.$refs.utilityBarControlBtn?.focus();
      const popOver = this.$refs.MyAccountPopover;
      popOver.removeEventListener('keydown', this.focusPopoverContent);
      popOver.removeEventListener('keyup', this.handleKeyup);
    },
    focusPopoverContent() {
      const popOver = this.$refs.MyAccountPopover;
      const focusableElements = getAllFocusableElements(popOver);
      popOver.addEventListener('keydown', (event) => focusTrap(event, focusableElements));
      popOver.addEventListener('keyup', this.handleKeyup);
      focusableElements[0]?.focus();
    },
    handleKeyup(event) {
      if (event.key === 'Escape') {
        this.$refs.utilityBarControlBtn?._tippy.hide();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.nav-utility-bar-control {
  &__icon {
    & .flag-icon-us-squared::after {
      margin-bottom: 0 !important;
    }
  }
}
</style>
